import React, { useEffect, useMemo, useState } from "react";
import { useStytch, useStytchUser } from "@stytch/react";
import {
  useNavigate,
  useLocation,
  Routes,
  Route,
  Link,
} from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  Contacts as ContactsIcon,
  Folder as FolderIcon,
  Build as BuildIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Memory as MemoryIcon,
  TheaterComedy as TheaterComedyIcon,
  Videocam as VideocamIcon,
} from "@mui/icons-material";
import TerminalIcon from "@mui/icons-material/Terminal";
import UsersTab from "./dashboard/UsersTab";
import ConversationsTab from "./dashboard/ConversationsTab";
import "./Dashboard.css";
import UserView from "./dashboard/users/UserView";
import ConversationView from "./dashboard/conversations/ConversationView";
import RolodexTab from "./dashboard/RolodexTab";
import RolodexView from "./dashboard/rolodex/RolodexView";
import OrganizationsTab from "./dashboard/OrganizationsTab";
import OrganizationView from "./dashboard/organizations/OrganizationView";
import DeploymentView from "./dashboard/deployments/DeploymentView";
import CreateDeploymentForm from "./dashboard/deployments/CreateDeploymentForm";
import BuildsTab from "./dashboard/BuildsTab";
import BuildView from "./dashboard/builds/BuildView";
import BehaviorConfigsTab from "./dashboard/BehaviorConfigsTab";
import BehaviorConfigView from "./dashboard/behavior-configs/BehaviorConfigView";
import AgentConfigsTab from "./dashboard/AgentConfigsTab";
import AgentConfigView from "./dashboard/agent-configs/AgentConfigView";
import ScheduleInspector from "./dashboard/ScheduleInspectorTab";
import SessionScheduleView from "./dashboard/schedule/SessionScheduleView";
import SessionsTab from "./dashboard/SessionsTab";
import { SessionDetailView } from "./dashboard/sessions";
import { NotFound } from "../App";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { debounce } from "lodash";
import { fetchUser } from "../redux/actions/baseActions";
import OpsConsoleTab from "./dashboard/OpsConsoleTab";
import AccessDenied from "./dashboard/AccessDenied";
import AssetTab from "./dashboard/AssetTab";
import AssetView from "./dashboard/assets/AssetView";
import AssessmentsTab from "./dashboard/AssessmentsTab";
import AssessmentView from "./dashboard/assessments/AssessmentView";
import AssessmentFormView from "./dashboard/assessments/AssessmentFormEditor";
import AssessedSessionView from "./dashboard/assessments/AssessedSessionView";
import MemoryHealthTab from "./dashboard/MemoryHealthTab";
import MemoryHealthView from "./dashboard/memory-health/MemoryHealthView";
import RoleplaysTab from "./dashboard/RoleplaysTab";
import RoleplayView from "./dashboard/roleplays/RoleplayView";
import SelectorExperiencesTab from "./dashboard/SelectorExperiencesTab";
import SelectorExperienceView from "./dashboard/selector-experiences/SelectorExperienceView";

const drawerWidth = 240;

interface NavItem {
  label: string;
  value: string;
  path: string;
  icon: React.ReactElement;
  permission: (adminUser: any) => boolean;
}

const Dashboard: React.FC = () => {
  const stytch = useStytch();
  const { user } = useStytchUser();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const {
    user: adminUser,
    loading: userLoading,
    error: userError,
  } = useSelector((state: RootState) => state.base);

  const debouncedFetchUser = useMemo(
    () => debounce(() => dispatch(fetchUser()), 300),
    [dispatch]
  );

  useEffect(() => {
    if (user) {
      debouncedFetchUser();
    }
    return () => {
      debouncedFetchUser.cancel();
    };
  }, [debouncedFetchUser, user]);

  const handleLogout = async () => {
    await stytch.session.revoke();
    navigate("/login");
    localStorage.removeItem("jwt");
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get("token");
    const tokenType = queryParams.get("stytch_token_type");
    if (!user && !(token || tokenType)) {
      navigate(
        `/login?redirect=${encodeURIComponent(
          location.pathname + location.search
        )}`
      );
    }
  }, [user, navigate, location]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const redirect = queryParams.get("redirect");
    if (redirect && user) {
      navigate(redirect);
    }
  }, [user, navigate, location]);

  // Navigation items configuration
  const navItems: NavItem[] = [
    {
      label: "Organizations",
      value: "organizations",
      path: "/dashboard/organizations",
      icon: <BusinessIcon />,
      permission: (adminUser) =>
        adminUser?.scopes?.includes("organizationview:view"),
    },
    {
      label: "Users",
      value: "users",
      path: "/dashboard/users",
      icon: <PeopleIcon />,
      permission: (adminUser) => adminUser?.scopes?.includes("userview:view"),
    },
    {
      label: "Conversations",
      value: "conversations",
      path: "/dashboard/conversations",
      icon: <ChatIcon />,
      permission: (adminUser) =>
        adminUser?.scopes?.includes("conversationview:view") ||
        adminUser?.scopes?.includes("annotationview:view"),
    },
    {
      label: "Rolodex",
      value: "rolodex",
      path: "/dashboard/rolodex",
      icon: <ContactsIcon />,
      permission: (adminUser) =>
        adminUser?.scopes?.includes("rolodexview:view"),
    },
    {
      label: "Assets",
      value: "assets",
      path: "/dashboard/assets",
      icon: <FolderIcon />,
      permission: (adminUser) => adminUser?.scopes?.includes("assetview:view"),
    },
    {
      label: "Memory Health",
      value: "memory-health",
      path: "/dashboard/memory-health",
      icon: <MemoryIcon />,
      permission: (adminUser) => adminUser?.scopes?.includes("assetview:view"),
    },
    {
      label: "Builds",
      value: "builds",
      path: "/dashboard/builds",
      icon: <BuildIcon />,
      permission: (adminUser) => adminUser?.scopes?.includes("buildview:view"),
    },
    {
      label: "Agent Configs",
      value: "agent-configs",
      path: "/dashboard/agent-configs",
      icon: <SettingsIcon />,
      permission: (adminUser) =>
        adminUser?.scopes?.includes("agentconfigview:view"),
    },
    {
      label: "Session Schedule",
      value: "session-schedule",
      path: "/dashboard/session-schedule",
      icon: <ScheduleIcon />,
      permission: (adminUser) =>
        adminUser?.scopes?.includes("scheduleview:view"),
    },
    {
      label: "Sessions",
      value: "sessions",
      path: "/dashboard/sessions",
      icon: <VideocamIcon />,
      permission: (adminUser) =>
        adminUser?.scopes?.includes("scheduleview:view"),
    },
    {
      label: "Assessments",
      value: "assessments",
      path: "/dashboard/assessments",
      icon: <AssignmentIcon />,
      permission: (adminUser) =>
        adminUser?.scopes?.includes("resbase:read"),
    },
    {
      label: "Roleplays",
      value: "roleplays",
      path: "/dashboard/roleplays",
      icon: <TheaterComedyIcon />,
      permission: (adminUser) =>
        adminUser?.scopes?.includes("resbase:read"),
    },
    {
      label: "Selector Experiences",
      value: "selector-experiences",
      path: "/dashboard/selector-experiences",
      icon: <AssignmentIcon />,
      permission: (adminUser) =>
        adminUser?.scopes?.includes("selectorexperience:read"),
    },
    {
      label: "Ops Console",
      value: "ops-console",
      path: "/dashboard/ops-console",
      icon: <TerminalIcon />,
      permission: (adminUser) =>
        adminUser?.scopes?.includes("opsconsoleview:view"),
    },
  ];

  // Filter nav items based on permissions
  const visibleNavItems = navItems.filter((item) => item.permission(adminUser));

  // Determine active nav item based on current path
  const getActiveNavValue = () => {
    const currentPath = location.pathname;
    const activeItem = navItems.find((item) =>
      currentPath.startsWith(item.path)
    );
    return activeItem?.value || "";
  };

  const activeNavValue = getActiveNavValue();

  // Handle navigation
  const handleNavClick = (path: string) => {
    navigate(path + location.search);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Redirect to first available tab if on /dashboard
  useEffect(() => {
    if (
      adminUser &&
      location.pathname === "/dashboard" &&
      visibleNavItems.length > 0
    ) {
      navigate(visibleNavItems[0].path + location.search);
    }
  }, [
    adminUser,
    location.pathname,
    location.search,
    navigate,
    visibleNavItems,
  ]);

  if (userLoading) {
    return <div>Loading...</div>;
  }
  if (!user) {
    return <NotFound />;
  }

  if (userError) {
    return <div>Error: {userError}</div>;
  }

  const hasAnyAccess = visibleNavItems.length > 0;

  // If the user has no access, redirect to the Access Denied page
  if (!hasAnyAccess) {
    return <AccessDenied />;
  }

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ p: 2.5, textAlign: "center" }}>
        <Typography variant="h5" sx={{ color: "#ffffff", fontWeight: 600 }}>
          Admin Portal
        </Typography>
      </Box>
      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)" }} />
      <List sx={{ flexGrow: 1 }}>
        {visibleNavItems.map((item) => (
          <ListItem key={item.value} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={activeNavValue === item.value}
              onClick={() => handleNavClick(item.path)}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "rgba(99, 179, 237, 0.1)",
                  borderLeft: "3px solid #63b3ed",
                  "&:hover": {
                    backgroundColor: "rgba(99, 179, 237, 0.15)",
                  },
                },
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                },
                paddingLeft: activeNavValue === item.value ? "21px" : "24px",
                transition: "all 0.2s ease",
              }}
            >
              <ListItemIcon
                sx={{
                  color: activeNavValue === item.value ? "#63b3ed" : "#a0aec0",
                  minWidth: "40px",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                sx={{
                  "& .MuiListItemText-primary": {
                    color:
                      activeNavValue === item.value ? "#ffffff" : "#a0aec0",
                    fontWeight: activeNavValue === item.value ? 600 : 400,
                    fontSize: "0.95rem",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.08)" }} />
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleLogout}
          sx={{
            color: "#a0aec0",
            borderColor: "rgba(255, 255, 255, 0.15)",
            "&:hover": {
              borderColor: "rgba(255, 255, 255, 0.25)",
              backgroundColor: "rgba(255, 255, 255, 0.03)",
            },
          }}
        >
          Log out
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      {/* Mobile AppBar */}
      <AppBar
        position="fixed"
        sx={{
          display: { md: "none" },
          backgroundColor: "#2d3748",
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="body1"
            sx={{ flexGrow: 1, color: "#ffffff", fontSize: "1.1rem" }}
          >
            Admin Portal
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="navigation"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#2d3748",
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#2d3748",
              borderRight: "1px solid rgba(255, 255, 255, 0.08)",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: "#f7fafc",
          minHeight: "100vh",
        }}
      >
        {/* Add toolbar spacing on mobile */}
        <Toolbar sx={{ display: { md: "none" } }} />
        <Routes>
          {adminUser?.scopes?.includes("userview:view") && (
            <>
              <Route path="users/:userId" element={<UserView />} />
              <Route path="users" element={<UsersTab />} />
            </>
          )}
          {(adminUser?.scopes?.includes("conversationview:view") ||
            adminUser?.scopes?.includes("annotationview:view")) && (
              <>
                <Route
                  path="conversations/:conversationId"
                  element={
                    <ConversationView
                      canReadConversation={adminUser?.scopes?.includes(
                        "conversationview:view"
                      )}
                    />
                  }
                />
                <Route
                  path="conversations"
                  element={
                    <ConversationsTab
                      canReadConversation={adminUser?.scopes?.includes(
                        "conversationview:view"
                      )}
                    />
                  }
                />
              </>
            )}
          {adminUser?.scopes?.includes("rolodexview:view") && (
            <>
              <Route path="rolodex" element={<RolodexTab />} />
              <Route path="rolodex/:personId" element={<RolodexView />} />
            </>
          )}
          {adminUser?.scopes?.includes("assetview:view") && (
            <>
              <Route path="assets" element={<AssetTab />} />
              <Route
                path="assets/:namespace/:assetId"
                element={<AssetView />}
              />
              <Route path="memory-health" element={<MemoryHealthTab />} />
              <Route
                path="memory-health/:namespace"
                element={<MemoryHealthView />}
              />
            </>
          )}
          {adminUser?.scopes?.includes("organizationview:view") && (
            <>
              <Route path="organizations" element={<OrganizationsTab />} />
              <Route
                path="organizations/:organizationId"
                element={<OrganizationView />}
              />
            </>
          )}
          {adminUser?.scopes?.includes("deploymentview:view") && (
            <>
              <Route
                path="deployments/create"
                element={<CreateDeploymentForm />}
              />
              <Route
                path="deployments/:deploymentId"
                element={<DeploymentView />}
              />
            </>
          )}
          {adminUser?.scopes?.includes("buildview:view") && (
            <>
              <Route path="builds" element={<BuildsTab />} />
              <Route path="builds/:buildId" element={<BuildView />} />
            </>
          )}
          {adminUser?.scopes?.includes("behaviorconfigview:view") && (
            <>
              <Route path="behavior-configs" element={<BehaviorConfigsTab />} />
              <Route
                path="behavior-configs/:behaviorConfigId"
                element={<BehaviorConfigView />}
              />
            </>
          )}
          {adminUser?.scopes?.includes("agentconfigview:view") && (
            <>
              <Route path="agent-configs" element={<AgentConfigsTab />} />
              <Route
                path="agent-configs/:agentConfigId"
                element={<AgentConfigView />}
              />
            </>
          )}
          {adminUser?.scopes?.includes("scheduleview:view") && (
            <>
              <Route path="session-schedule" element={<ScheduleInspector />} />
              <Route
                path="session-schedule/:sessionId"
                element={<SessionScheduleView />}
              />
              <Route path="sessions" element={<SessionsTab />} />
              <Route
                path="sessions/:sessionId"
                element={<SessionDetailView />}
              />
            </>
          )}
          {adminUser?.scopes?.includes("resbase:read") && (
            <>
              <Route path="assessments" element={<AssessmentsTab />} />
              <Route
                path="assessments/:organizationId/:assessmentId"
                element={<AssessmentView />}
              />
              <Route
                path="assessments/:organizationId/:assessmentId/form"
                element={<AssessmentFormView />}
              />
              <Route path="roleplays" element={<RoleplaysTab />} />
              <Route
                path="roleplays/:organizationId/:roleplayId"
                element={<RoleplayView />}
              />
              <Route
                path="roleplays/:organizationId/:roleplayId/sessions/:sessionId"
                element={<AssessedSessionView />}
              />
            </>
          )}
          {adminUser?.scopes?.includes("opsconsoleview:view") && (
            <>
              <Route path="ops-console" element={<OpsConsoleTab />} />
            </>
          )}
          {adminUser?.scopes?.includes("selectorexperience:read") && (
            <>
              <Route path="selector-experiences" element={<SelectorExperiencesTab />} />
              <Route
                path="selector-experiences/:organizationId/:selectorId"
                element={<SelectorExperienceView />}
              />
            </>
          )}
          {adminUser?.scopes?.includes("assessmentview:view") && (
            <Route
              path="assessments/:assessmentId/sessions/:sessionId"
              element={<AssessedSessionView />}
            />
          )}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default Dashboard;
